import { useCallback, useEffect, useState } from 'react';

import { TransferItem, TransferJob } from '../../entities/transfer';
import { getTransferJob, retryTransferJob } from '../api/transfer';
import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

interface ItemProgress {
  status: TransferItem['transferStatus'];
  error?: string;
}

interface ContainerProgress {
  status: 'creating' | 'created' | 'failed';
  step?: string;
  error?: string;
}

export function useTransferJob(jobId: string | undefined) {
  const userId = auth.currentUser?.uid ?? 'anonymous';
  const { on, off } = useSocket(userId);

  const [job, setJob] = useState<TransferJob | null>(null);
  const [itemProgress, setItemProgress] = useState<Record<string, ItemProgress>>({});
  const [containerProgress, setContainerProgress] = useState<ContainerProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTransferJob(jobId)
      .then((result) => {
        if (!cancelled) setJob(result);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load transfer job.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;

    const onJobUpdated = (data: { userId: string; job: TransferJob }) => {
      if (data.job._id !== jobId) return;
      setJob(data.job);
    };

    const onItemProgress = (data: {
      jobId: string;
      itemId: string;
      status: TransferItem['transferStatus'];
      error?: string;
    }) => {
      if (data.jobId !== jobId) return;
      // The container is done by the time any item starts (transfer-contract.md §3) — clear its
      // progress line so item rows take over instead of lingering underneath them.
      setContainerProgress(null);
      setItemProgress((prev) => ({ ...prev, [data.itemId]: { status: data.status, error: data.error } }));
    };

    const onContainerProgress = (data: {
      jobId: string;
      status: 'creating' | 'created' | 'failed';
      step?: string;
      error?: string;
    }) => {
      if (data.jobId !== jobId) return;
      setContainerProgress({ status: data.status, step: data.step, error: data.error });
    };

    on('transferJobUpdated', onJobUpdated);
    on('transferItemProgress', onItemProgress);
    on('transferContainerProgress', onContainerProgress);
    return () => {
      off('transferJobUpdated', onJobUpdated as (...args: unknown[]) => void);
      off('transferItemProgress', onItemProgress as (...args: unknown[]) => void);
      off('transferContainerProgress', onContainerProgress as (...args: unknown[]) => void);
    };
  }, [jobId, on, off]);

  const retry = useCallback(async () => {
    if (!jobId) return;
    const updated = await retryTransferJob(jobId);
    setJob(updated);
    return updated;
  }, [jobId]);

  return { job, itemProgress, containerProgress, loading, error, retry };
}
